import type { ReactNode } from 'react';
import { App, Card, Skeleton, Switch, Typography } from 'antd';
import { CheckSquareOutlined, FormOutlined, FunnelPlotOutlined, HolderOutlined, IdcardOutlined, InboxOutlined, MailOutlined } from '@ant-design/icons';
import { DndContext, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { LoadError } from '../../components/states';
import { errorMessage } from '../../api/client';
import { useSettings, useUpdateSettings, type FeatureKey, type Settings } from '../../features/settings/api';
import { palette } from '../../app/theme';

type Feature = { key: FeatureKey; title: string; icon: ReactNode; description: string; whenOff: string };

const FEATURES: Feature[] = [
  {
    key: 'campaigns',
    title: 'Campaigns & templates',
    icon: <MailOutlined />,
    description: 'Design email templates and send campaigns to lists and segments.',
    whenOff: 'Hides Campaigns and Templates, the dashboard campaign card and the campaign send settings on Verification.',
  },
  {
    key: 'forms',
    title: 'Forms',
    icon: <FormOutlined />,
    description: 'Hosted and embeddable forms that create or update contacts.',
    whenOff: 'Hides Forms, and every public form link stops accepting submissions.',
  },
  {
    key: 'pipelines',
    title: 'Sales pipelines',
    icon: <FunnelPlotOutlined />,
    description: 'Deals moving through pipeline stages, with a kanban board.',
    whenOff: 'Hides Pipeline, deals on profiles and search, Settings → Pipelines and the dashboard deal cards. Forms stop opening deals.',
  },
  {
    key: 'inbox',
    title: 'Centralized inbox',
    icon: <InboxOutlined />,
    description: 'One shared inbox for replies across every email domain.',
    whenOff: 'Hides Inbox and its unread badge. Replies are still received and stay on each contact’s Emails tab.',
  },
  {
    key: 'tasks',
    title: 'Task management',
    icon: <CheckSquareOutlined />,
    description: 'Projects’ tasks in list, board and calendar views, with sections, subtasks, comments, files, My tasks and an activity feed.',
    whenOff: 'Hides Tasks, My tasks and Task activity. Task lists on contacts, organizations and deals keep working.',
  },
  {
    key: 'cardScanner',
    title: 'Scan cards',
    icon: <IdcardOutlined />,
    description: 'Photograph business cards with a phone; the details are read for you to check, then saved as contacts tagged with who added them.',
    whenOff: 'Hides Scan cards. Contacts already saved from cards keep their tags and notes.',
  },
];

export function FeaturesPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const settings = useSettings();
  const update = useUpdateSettings();
  const reorder = useUpdateSettings();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const features = settings.data?.features;
  const order = settings.data?.featureOrder ?? [];
  const sorted = [...FEATURES].sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));

  const toggle = async (key: FeatureKey, on: boolean) => {
    if (!features) return;
    try {
      await update.mutateAsync({ features: { ...features, [key]: on } });
      message.success(`${FEATURES.find((f) => f.key === key)?.title} ${on ? 'turned on' : 'turned off'}`);
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  // shown at once (the menu moves too), put back if saving fails
  const onDragEnd = async ({ active, over }: DragEndEvent) => {
    if (!settings.data || !over || active.id === over.id) return;
    const previous = settings.data;
    const keys = sorted.map((f) => f.key);
    const featureOrder = arrayMove(keys, keys.indexOf(active.id as FeatureKey), keys.indexOf(over.id as FeatureKey));
    qc.setQueryData<Settings>(['settings'], { ...previous, featureOrder });
    try {
      await reorder.mutateAsync({ featureOrder });
    } catch (e) {
      qc.setQueryData<Settings>(['settings'], (now) => (now ? { ...now, featureOrder: previous.featureOrder } : now));
      message.error(errorMessage(e));
    }
  };

  return (
    <div className="page page-narrow">
      <PageHeader
        title="Features"
        subtitle="Turn parts of the CRM on or off for everyone. Switching a feature off hides it; its data is kept and comes back when you switch it on again. Drag features to set their order in the menu."
      />
      {settings.isError ? (
        <Card>
          <LoadError error={settings.error} onRetry={settings.refetch} title="Could not load settings" />
        </Card>
      ) : !features ? (
        <Card>
          <Skeleton active />
        </Card>
      ) : (
        <Card styles={{ body: { padding: 0 } }}>
          <DndContext sensors={sensors} onDragEnd={(e) => void onDragEnd(e)}>
            <SortableContext items={sorted.map((f) => f.key)} strategy={verticalListSortingStrategy}>
              {sorted.map((f, i) => (
                <FeatureRow key={f.key} feature={f} first={i === 0}>
                  <Switch
                    aria-label={f.title}
                    checked={features[f.key]}
                    loading={update.isPending && update.variables?.features?.[f.key] !== features[f.key]}
                    disabled={update.isPending}
                    onChange={(on) => void toggle(f.key, on)}
                  />
                </FeatureRow>
              ))}
            </SortableContext>
          </DndContext>
        </Card>
      )}
    </div>
  );
}

function FeatureRow({ feature: f, first, children }: { feature: Feature; first: boolean; children: ReactNode }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: f.key });
  const on = useSettings().data?.features[f.key];
  return (
    <div
      ref={setNodeRef}
      style={{
        display: 'flex',
        gap: 16,
        alignItems: 'flex-start',
        padding: '20px 24px 20px 12px',
        borderTop: first ? undefined : `1px solid ${palette.border}`,
        transform: CSS.Translate.toString(transform && { ...transform, x: 0 }),
        transition,
        position: 'relative',
        zIndex: isDragging ? 1 : undefined,
        background: isDragging ? palette.primarySoft : undefined,
      }}
    >
      <span
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        aria-label={`Move ${f.title}`}
        style={{ cursor: isDragging ? 'grabbing' : 'grab', color: palette.textTertiary, padding: '4px 2px', marginRight: -8, touchAction: 'none' }}
      >
        <HolderOutlined />
      </span>
      <div style={{ fontSize: 20, color: on ? palette.primary : palette.textTertiary, paddingTop: 2 }}>{f.icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Typography.Text strong>{f.title}</Typography.Text>
        <div>
          <Typography.Text type="secondary">{f.description}</Typography.Text>
        </div>
        <div style={{ marginTop: 4 }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            When off: {f.whenOff}
          </Typography.Text>
        </div>
      </div>
      {children}
    </div>
  );
}
